import type { TFunction } from "i18next";
import { useState } from "react";
import type { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";
import {
  emptyCreditApplication,
  u3125MaxCreditApplicationAmountForRow,
  u3125MaxCreditApplicationNonTaxableForRow,
  u3125MaxCreditApplicationTaxableForRow,
  u3125MergeCreditSourcesForDisplay,
  u3125RoundTaxAmount,
  u3125ValidateCreditApplicationForm,
  type U3125CreditSource,
  type U3125InvoiceCreditApplicationGridRow,
  type U3125InvoiceFormRow,
} from "../../../../U3125Service";
import type { U3125FactureNestedParentHandlers } from "../u3125FactureInvoiceLinesTab/U3125InvoiceLineHandlers";

export interface U3125CreditApplicationHandlerReturn {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onRowClick: (row: U3125InvoiceCreditApplicationGridRow) => void;
  onDlgClose: (result: string) => void;
  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3125InvoiceCreditApplicationGridRow>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3125InvoiceCreditApplicationGridRow>>;
  dataChangedEvent: number;
  dialogBaselineAmount: number;
  dialogBaselineTaxable: number;
  dialogBaselineNonTaxable: number;
  dialogOpenSourceId: string;
  dialogEditingRowId: string;
}

export interface U3125CreditApplicationHandlerParam {
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3125InvoiceCreditApplicationGridRow>>;
  t: TFunction;
  ns: string;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3125InvoiceCreditApplicationGridRow>>;
  parentFormHandlers: U3125FactureNestedParentHandlers;
}

export function useU3125CreditApplicationHandler(params: U3125CreditApplicationHandlerParam): U3125CreditApplicationHandlerReturn {
  const { gridStateHandlers, formStateHandlers, parentFormHandlers, t, ns } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);
  const [dialogBaselineAmount, setDialogBaselineAmount] = useState(0);
  const [dialogBaselineTaxable, setDialogBaselineTaxable] = useState(0);
  const [dialogBaselineNonTaxable, setDialogBaselineNonTaxable] = useState(0);
  const [dialogOpenSourceId, setDialogOpenSourceId] = useState("");
  const [dialogEditingRowId, setDialogEditingRowId] = useState("");

  const getAvailableSources = (): U3125CreditSource[] => {
    const inv = parentFormHandlers.getData();
    const raw = inv?.availableCreditSources ?? [];
    return u3125MergeCreditSourcesForDisplay(raw, inv?.creditApplications ?? []);
  };

  const enrichFromSource = (
    row: U3125InvoiceCreditApplicationGridRow,
    source: U3125CreditSource | undefined,
  ): U3125InvoiceCreditApplicationGridRow => {
    if (!source) return row;
    return {
      ...row,
      sourceNature: source.nature,
      sourceInvoiceNumber: source.invoiceNumber,
      sourceInvoiceDate: source.invoiceDate,
      sourceInvoiceTypeCode: source.invoiceTypeCode,
    };
  };

  const setDialogBaselines = (row: U3125InvoiceCreditApplicationGridRow): void => {
    setDialogBaselineAmount(Math.abs(Number(row.amount) || 0));
    setDialogBaselineTaxable(Math.abs(Number(row.amountTaxable) || 0));
    setDialogBaselineNonTaxable(Math.abs(Number(row.amountNonTaxable) || 0));
    setDialogOpenSourceId(row.sourceInvoiceId?.trim() ?? "");
    setDialogEditingRowId(row.id?.trim() ?? "");
  };

  const onAdd = (): void => {
    const inv = parentFormHandlers.getData();
    const n = inv?.creditApplications?.length ?? 0;
    formStateHandlers.setData(emptyCreditApplication(n));
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    setDialogBaselineAmount(0);
    setDialogBaselineTaxable(0);
    setDialogBaselineNonTaxable(0);
    setDialogOpenSourceId("");
    setDialogEditingRowId("");
    setDialogOpen(true);
  };

  const onModify = (): void => {
    const selectedRow = gridStateHandlers.getSelectedRows()[0] as U3125InvoiceCreditApplicationGridRow | undefined;
    if (!selectedRow) return;
    formStateHandlers.setData({ ...selectedRow });
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    setDialogBaselines(selectedRow);
    setDialogOpen(true);
  };

  const onDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  const onRowClick = (row: U3125InvoiceCreditApplicationGridRow): void => {
    formStateHandlers.setData({ ...row });
    const parentFormState = parentFormHandlers.getFormState?.();
    if (parentFormState === U2000FormStateEnumState.EDIT) {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      setDialogBaselines(row);
    } else {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.NONE);
      setDialogBaselineAmount(0);
      setDialogBaselineTaxable(0);
      setDialogBaselineNonTaxable(0);
      setDialogOpenSourceId("");
      setDialogEditingRowId("");
    }
    setDialogOpen(true);
  };

  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
      return;
    }
    if (result !== "save") {
      setDialogOpen(false);
      return;
    }

    const draft = formStateHandlers.getData();
    const availableSources = getAvailableSources();
    const source = availableSources.find((item) => item.invoiceId === draft.sourceInvoiceId?.trim());
    const invoice = parentFormHandlers.getData() as U3125InvoiceFormRow | null;
    const sameSource = draft.sourceInvoiceId?.trim() === dialogOpenSourceId;
    const editingId = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT ? dialogEditingRowId : undefined;

    let errors: Record<string, string> = {};
    if (source?.nature === "CREDIT") {
      const baselineTaxable = sameSource ? dialogBaselineTaxable : 0;
      const baselineNonTaxable = sameSource ? dialogBaselineNonTaxable : 0;
      errors = u3125ValidateCreditApplicationForm(
        draft,
        source,
        {
          mode: "CREDIT",
          maxTaxable: u3125MaxCreditApplicationTaxableForRow(source, baselineTaxable, invoice, editingId),
          maxNonTaxable: u3125MaxCreditApplicationNonTaxableForRow(source, baselineNonTaxable, invoice, editingId),
        },
        t,
        ns,
      );
    } else {
      const baselineAmount = sameSource ? dialogBaselineAmount : 0;
      errors = u3125ValidateCreditApplicationForm(
        draft,
        source,
        { mode: "ADVANCE", maxAmount: u3125MaxCreditApplicationAmountForRow(source, baselineAmount) },
        t,
        ns,
      );
    }

    formStateHandlers.setErrors(errors);
    if (Object.keys(errors).length > 0) return;

    let saved: U3125InvoiceCreditApplicationGridRow;
    if (source?.nature === "CREDIT") {
      const amountTaxable = u3125RoundTaxAmount(Math.abs(Number(draft.amountTaxable) || 0));
      const amountNonTaxable = u3125RoundTaxAmount(Math.abs(Number(draft.amountNonTaxable) || 0));
      saved = enrichFromSource(
        { ...draft, amountTaxable, amountNonTaxable, amount: u3125RoundTaxAmount(amountTaxable + amountNonTaxable) },
        source,
      );
    } else {
      saved = enrichFromSource({ ...draft, amount: Math.abs(Number(draft.amount) || 0) }, source);
    }

    if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT) {
      const updatedData = gridStateHandlers.getData().map((item) => (item.id === saved.id ? { ...saved } : item));
      gridStateHandlers.setData(updatedData);
      setDataChangedEvent((prev) => prev + 1);
    } else if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD) {
      const newData = { ...saved, id: generateTemporaryId() } as U3125InvoiceCreditApplicationGridRow;
      gridStateHandlers.setData([...gridStateHandlers.getData(), newData]);
      gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
      setDataChangedEvent((prev) => prev + 1);
    }
    formStateHandlers.afterSaveData(saved);
    setDialogOpen(false);
  };

  const onDlgDeleteClose = (result: string): void => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((selected) => selected.id === row.id));
      gridStateHandlers.setData(remainingRows);
      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent((prev) => prev + 1);
    }
    setDeleteDialogOpen(false);
  };

  return {
    onAdd,
    onModify,
    onDelete,
    onRowClick,
    onDlgClose,
    isDialogOpen,
    setDialogOpen,
    formStateHandlers,
    gridStateHandlers,
    isDeleteDialogOpen,
    onDlgDeleteClose,
    dataChangedEvent,
    dialogBaselineAmount,
    dialogBaselineTaxable,
    dialogBaselineNonTaxable,
    dialogOpenSourceId,
    dialogEditingRowId,
  };
}
